import { CheckoutIntentKind } from './checkout-intent-kind';
import { CheckoutVerdict, verdictFromConfirm } from './elements-checkout-flow';
import { StripeInstance } from './stripe/embedded';

/**
 * Settling a purchase for a buyer Stripe sent away and sent back.
 *
 * Most cards never leave the page — `confirmPayment` runs with `redirect: 'if_required'` — but a
 * bank redirect method, and some 3-D Secure flows, do. Those buyers return to our own URL with the
 * intent's client secret in the query string, and NOT with a Checkout Session id.
 *
 * That distinction is the whole reason this module exists. The picker's existing return handler
 * knows only Checkout Sessions: it reads `session_id`, and anything without one it calls
 * `incomplete`. A returning Elements buyer would therefore be told their payment did not go
 * through — for a payment that may well have succeeded and that the webhook may already have
 * granted. There is no single "return handler for both lanes"; there are two, and this is the other.
 */

/** The marker our own return URL carries, so the handler knows which lane is coming back. */
export const ELEMENTS_LANE_PARAM = 'lane';
export const ELEMENTS_LANE_VALUE = 'elements';

export interface ElementsReturn {
  clientSecret: string;
  /** Which intent it is — Stripe uses a different query parameter for each. */
  kind: CheckoutIntentKind;
  /** Finalized amount carried across a bank redirect for paid-only revenue analytics. */
  amountMinor?: number;
  currency?: string;
}

/**
 * The return we have not settled yet. It is written before confirm because a redirect is a full
 * navigation, and the URL Stripe returns contains the intent secret but not the finalized quote.
 * Keeping both in one record also makes clearing it the once-only analytics latch.
 */
const PENDING_RETURN_KEY = 'orion.pendingElementsReturn';
let inMemoryReturn: ElementsReturn | null = null;

export function stashElementsReturn(ret: ElementsReturn): boolean {
  inMemoryReturn = ret;
  try {
    sessionStorage.setItem(PENDING_RETURN_KEY, JSON.stringify(ret));
    return true;
  } catch {
    return false;
  }
}

export function readStashedElementsReturn(): ElementsReturn | null {
  try {
    const raw = sessionStorage.getItem(PENDING_RETURN_KEY);
    if (!raw) return inMemoryReturn;
    const parsed = JSON.parse(raw) as {
      clientSecret?: unknown;
      kind?: unknown;
      amountMinor?: unknown;
      currency?: unknown;
    };
    if (typeof parsed.clientSecret !== 'string' || typeof parsed.kind !== 'string') {
      return null;
    }
    return {
      clientSecret: parsed.clientSecret,
      kind: parsed.kind as CheckoutIntentKind,
      ...(typeof parsed.amountMinor === 'number' && Number.isFinite(parsed.amountMinor)
        ? { amountMinor: parsed.amountMinor }
        : {}),
      ...(typeof parsed.currency === 'string' && parsed.currency ? { currency: parsed.currency } : {}),
    };
  } catch {
    return inMemoryReturn;
  }
}

export function clearStashedElementsReturn(): void {
  inMemoryReturn = null;
  try {
    sessionStorage.removeItem(PENDING_RETURN_KEY);
  } catch {
    /* nothing to clear */
  }
}

/**
 * Read an Elements return out of a query string, or null when this is not one.
 *
 * Pure, so the parameter names — which are Stripe's, not ours — can be pinned by a test rather than
 * trusted. Getting either of them wrong reproduces exactly the bug this module fixes.
 */
export function readElementsReturn(search: string): ElementsReturn | null {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  if (params.get(ELEMENTS_LANE_PARAM) !== ELEMENTS_LANE_VALUE) {
    return null;
  }
  const payment = params.get('payment_intent_client_secret');
  if (payment) {
    return { clientSecret: payment, kind: CheckoutIntentKind.CHECKOUT_INTENT_PAYMENT };
  }
  const setup = params.get('setup_intent_client_secret');
  if (setup) {
    return { clientSecret: setup, kind: CheckoutIntentKind.CHECKOUT_INTENT_SETUP };
  }
  // Our marker with no secret beside it. The buyer came back from somewhere, but nothing here says
  // what happened, and guessing in either direction is worse than admitting it.
  return null;
}

/**
 * Read the intent the buyer just came back from, and reduce it to the same verdict a confirm would
 * have produced — so a redirect purchase and an inline one end in the same place.
 *
 * A failed read is `unsettled`, never `declined`: the payment may have succeeded and the webhook may
 * already be granting, so a network blip on the way back must not tell the buyer they were refused.
 */
export async function resolveElementsReturn(
  stripe: StripeInstance,
  ret: ElementsReturn,
): Promise<CheckoutVerdict> {
  // A free top-up is already settled by prepare and has no intent to retrieve.
  if (ret.kind === CheckoutIntentKind.CHECKOUT_INTENT_NONE) {
    return ret.amountMinor === 0 ? 'paid' : 'unsettled';
  }
  try {
    const result = ret.kind === CheckoutIntentKind.CHECKOUT_INTENT_SETUP
      ? await stripe.retrieveSetupIntent(ret.clientSecret)
      : await stripe.retrievePaymentIntent(ret.clientSecret);
    return verdictFromConfirm(ret.kind, result);
  } catch {
    return 'unsettled';
  }
}
